import { Flag, CheckCircle2, HelpCircle, Lock } from "lucide-react";
import InfoTip from "./InfoTip";
import type { MilestoneEntry } from "@/lib/parent/commandMetrics";

interface Props {
  entries: MilestoneEntry[];
}

const ICON = {
  completed: CheckCircle2,
  quiz_due: HelpCircle,
  unlock: Lock,
} as const;

const COLOR = {
  completed: "text-emerald-300",
  quiz_due: "text-amber-300",
  unlock: "text-muted-foreground",
} as const;

export default function MilestoneTimeline({ entries }: Props) {
  return (
    <section className="relative h-full overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-card/80 via-card/60 to-card/40 p-5 backdrop-blur-2xl shadow-[0_24px_60px_-30px_hsl(var(--primary)/0.45),inset_0_1px_0_0_hsl(var(--foreground)/0.05)]">
      <div className="relative">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <Flag className="h-3.5 w-3.5" />
            </span>
            <h3 className="font-heading text-sm font-semibold tracking-tight text-foreground sm:text-base">Milestone Timeline</h3>
            <InfoTip title="Milestone Timeline" body="Recent completions and the next quizzes or module unlocks coming up." />
          </div>
        </div>

        {entries.length === 0 ? (
          <p className="mt-6 text-center text-xs text-muted-foreground">First lesson will appear here as a milestone.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {entries.map((e) => {
              const Icon = ICON[e.kind];
              return (
                <li key={e.id} className="flex items-start gap-2.5">
                  <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${COLOR[e.kind]}`} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-foreground">{e.title}</p>
                    <p className="text-[10px] text-muted-foreground">{e.detail}</p>
                  </div>
                  <span className="shrink-0 text-[10px] text-muted-foreground">{e.date}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}